import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, EmptyState, Pagination, SearchInput, Switch, TableSkeleton } from "../../../components/ui";
import {
  apiMessage,
  getMobileStatus,
  getMobileTransactions,
  lookupMobile,
  pingMobile,
  updateMobileSettings,
} from "../../../services/monitoring";
import { formatAmount, formatDate, KIND_LABELS, PaymentDetails, Timeline } from "./shared";

const STATUS_BADGES = {
  SUCCESS: { label: "Succeeded", color: "green" },
  FAILED: { label: "Failed", color: "red" },
  PENDING: { label: "Pending", color: "blue" },
};

const PROVIDER_LABELS = { pawapay: "pawaPay", cinetpay: "CinetPay", paystack: "Paystack" };
const PROVIDER_COLORS = { pawapay: "green", cinetpay: "violet", paystack: "blue" };

/** Pays (codes ISO) → nom affiché. */
const COUNTRY_NAMES = {
  CI: "Côte d'Ivoire", SN: "Sénégal", ML: "Mali", BF: "Burkina Faso", TG: "Togo", BJ: "Bénin", NE: "Niger",
  GW: "Guinée-Bissau", CM: "Cameroun", GA: "Gabon", CG: "Congo", TD: "Tchad", CF: "Centrafrique",
  GQ: "Guinée équatoriale", CD: "RD Congo", RW: "Rwanda", GH: "Ghana", KE: "Kenya", NG: "Nigeria",
  UG: "Ouganda", TZ: "Tanzanie", ZM: "Zambie", MW: "Malawi", MZ: "Mozambique", SL: "Sierra Leone",
  LS: "Lesotho", ET: "Éthiopie", GN: "Guinée",
};

const Check = ({ ok, children }) => (
  <li className="logs-check" data-ok={ok ? "" : undefined}>
    <i className={ok ? "ri-checkbox-circle-line" : "ri-close-line"} aria-hidden="true"></i>
    {children}
  </li>
);

const ProviderBadge = ({ provider }) => {
  const { t } = useTranslation();
  if (!provider.configured) return <Badge color="slate" dot>{t("Not configured")}</Badge>;
  if (provider.suspended) return <Badge color="amber" dot>{t("Suspended")}</Badge>;
  return <Badge color="green" dot>{t("Active")}</Badge>;
};

/** Un prestataire : interrupteur, configuration (sans clé), test de connexion, activité 24 h. */
const ProviderCard = ({ provider, onToggle }) => {
  const { t } = useTranslation();
  const [ping, setPing] = useState({});

  const test = async (currency) => {
    setPing((p) => ({ ...p, [currency]: { running: true } }));
    try {
      const result = await pingMobile(provider.id, currency);
      setPing((p) => ({ ...p, [currency]: result }));
    } catch (error) {
      setPing((p) => ({ ...p, [currency]: { ok: false, message: apiMessage(error, t("Something went wrong")) } }));
    }
  };

  const minutesSinceNotification = provider.lastNotificationAt
    ? (Date.now() - new Date(provider.lastNotificationAt).getTime()) / 60000
    : null;

  return (
    <section className="ui-card logs-panel logs-provider" aria-label={provider.label} data-off={!provider.available || undefined}>
      <header className="logs-panel-head">
        <div className="logs-provider-title">
          <h2>{provider.label}</h2>
          <ProviderBadge provider={provider} />
          {provider.mode && (
            <Badge color={["live", "production"].includes(provider.mode) ? "violet" : "slate"}>
              {["live", "production"].includes(provider.mode) ? t("Live mode") : t("test mode")}
            </Badge>
          )}
        </div>
        {provider.configured && (
          <Switch
            checked={!provider.suspended}
            onChange={(on) => onToggle(provider, !on)}
            label={t("Accept new payments")}
            onLabel={t("Accepting payments")}
            offLabel={t("Suspended")}
          />
        )}
      </header>

      {!provider.configured ? (
        <p className="ui-hint">
          {provider.id === "pawapay"
            ? t("Add PAWAPAY_API_TOKEN (and PAWAPAY_MODE=production) to the server .env and restart: pawaPay becomes available here, first in the routing order.")
            : provider.id === "paystack"
            ? t("Add PAYSTACK_SECRET_KEY to the server .env and restart: Paystack becomes available here.")
            : t("Add the CinetPay keys (API key and sites) to the server .env and restart.")}
        </p>
      ) : (
        <>
          <ul className="logs-checks">
            {provider.apiKeyConfigured !== undefined && <Check ok={provider.apiKeyConfigured}>{t("API key")}</Check>}
            {provider.apiTokenConfigured !== undefined && (
              <Check ok={provider.apiTokenConfigured && !provider.configurationError}>
                {t("API token")}
                {provider.companyName && <span className="ui-hint"> · {provider.companyName}</span>}
                <button type="button" className="logs-link" onClick={() => test("all")} disabled={ping.all?.running}>
                  {ping.all?.running ? t("Testing…") : t("Test the connection")}
                </button>
                {ping.all && !ping.all.running && ping.all.ok !== undefined && (
                  <span className="logs-ping" data-ok={ping.all.ok ? "" : undefined}>{ping.all.message}</span>
                )}
              </Check>
            )}
            {provider.configurationError && (
              <p className="ui-hint" data-error="">{t("Account configuration unreadable")} : {provider.configurationError}</p>
            )}
            {provider.secretKeyConfigured !== undefined && (
              <Check ok={provider.secretKeyConfigured}>
                {provider.id === "paystack" ? t("Secret key (API and webhook signature)") : t("Secret key (notification signature)")}
              </Check>
            )}
            {provider.id !== "pawapay" && provider.currencies.map((c) => (
              <Check key={c.currency} ok={c.configured}>
                {provider.id === "cinetpay" ? `${t("Site")} ${c.currency}` : c.currency} {c.siteId && <code>{c.siteId}</code>}
                {c.subunit !== undefined && <span className="ui-hint">× {c.subunit}</span>}
                {c.configured && (
                  <button type="button" className="logs-link" onClick={() => test(c.currency)} disabled={ping[c.currency]?.running}>
                    {ping[c.currency]?.running ? t("Testing…") : t("Test the connection")}
                  </button>
                )}
                {ping[c.currency] && !ping[c.currency].running && ping[c.currency].ok !== undefined && (
                  <span className="logs-ping" data-ok={ping[c.currency].ok ? "" : undefined}>
                    {ping[c.currency].ok ? `${t("Connected")} (${ping[c.currency].ms} ms)` : `${t("Failed")} : ${ping[c.currency].message || ping[c.currency].code}`}
                  </span>
                )}
              </Check>
            ))}
          </ul>
          <div className="logs-countries">
            <span className="ui-label">{t("Countries")}</span>
            {provider.countryDetails ? (
              provider.countryDetails.length ? (
                <ul className="logs-pawapay-countries">
                  {provider.countryDetails.map((c) => (
                    <li key={c.code}>
                      <strong>{c.name}</strong>
                      <span className="ui-hint">
                        {c.currency} · {c.providers.join(", ")}
                        {c.minAmount != null && ` · ${c.minAmount}–${c.maxAmount} ${c.currency}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="ui-hint">{t("No country open for deposits on the pawaPay account yet.")}</p>
              )
            ) : (
              <div className="ui-chips">
                {provider.countries.map((code) => (
                  <span key={code} className="ui-chip logs-chip-static">{COUNTRY_NAMES[code] || code}</span>
                ))}
              </div>
            )}
          </div>
          <dl className="logs-context">
            {provider.channels && (
              <>
                <dt>{t("Channels")}</dt>
                <dd>{provider.channels}</dd>
              </>
            )}
            <dt>{provider.id === "paystack" ? t("Webhook URL") : provider.id === "pawapay" ? t("Callback URL (deposits)") : t("Notification URL")}</dt>
            <dd><code>{provider.notifyUrl}</code></dd>
            <dt>{t("Return URL")}</dt>
            <dd><code>{provider.returnUrl}</code></dd>
          </dl>
          <div className="logs-mini-stats">
            <div><strong data-tone="green">{provider.last24h.success}</strong><span>{t("succeeded")}</span></div>
            <div><strong data-tone="red">{provider.last24h.failed}</strong><span>{t("failed")}</span></div>
            <div><strong data-tone="blue">{provider.last24h.pending}</strong><span>{t("pending")}</span></div>
            <div><strong>{provider.last24h.notifications}</strong><span>{t("notifications received")}</span></div>
          </div>
          <p className="logs-last">
            <i className="ri-time-line" aria-hidden="true"></i>
            {provider.lastNotificationAt
              ? `${t("Last notification")} : ${formatDate(provider.lastNotificationAt)}`
              : t("No notification received yet")}
          </p>
          {minutesSinceNotification !== null && minutesSinceNotification > 24 * 60 && provider.available && (
            <p className="ui-hint" data-error="">{t("No notification for more than 24 h: check the notification URL in the provider's back office.")}</p>
          )}
          {provider.last24h.invalidSignatures > 0 && (
            <p className="ui-hint" data-error="">
              {t("{{count}} notification(s) with an invalid signature (status always rechecked with the provider).", { count: provider.last24h.invalidSignatures })}
            </p>
          )}
          {provider.lastError && (
            <div className="logs-last-error">
              <span className="ui-label">{t("Last error")} · {formatDate(provider.lastError.at)}</span>
              <p>{provider.lastError.message}</p>
            </div>
          )}
        </>
      )}
      <a className="ui-btn ui-btn-secondary" href={provider.dashboardUrl} target="_blank" rel="noreferrer">
        <i className="ri-bank-line" aria-hidden="true"></i>
        {t("Open the {{name}} back office", { name: provider.label })}
      </a>
    </section>
  );
};

/**
 * Routage : ordre de préférence, et pour chaque pays le prestataire qui le sert : automatique
 * (ordre de préférence), un prestataire précis, ou pays fermé.
 */
const Routing = ({ status, onPriority, onCountry }) => {
  const { t } = useTranslation();
  const entries = Object.entries(status.countries || {});
  const coverage = status.coverage || [];
  // Tous les prestataires configurés sont proposés pour chaque pays, même s'ils ne le couvrent pas
  // encore (ex. pays pas encore ouvert sur le compte pawaPay) : le pays ferme en attendant puis
  // passe chez ce prestataire dès qu'il l'ouvre
  const configured = status.providers.filter((p) => p.configured);
  return (
    <section className="ui-card logs-panel">
      <header className="logs-panel-head">
        <h2>{t("Routing by country")}</h2>
      </header>
      <p className="ui-hint">
        {t("Each country is served by the first available provider in this order. A suspended provider is skipped: its countries switch to the other one when it covers them. You can also choose the provider of a country, or close it: a country tied to one provider closes when that provider is suspended.")}
      </p>
      <ol className="logs-priority">
        {status.priority.map((id, index) => {
          const provider = status.providers.find((p) => p.id === id);
          return (
            <li key={id}>
              <span className="logs-priority-rank">{index + 1}</span>
              <strong>{PROVIDER_LABELS[id] || id}</strong>
              {provider && <ProviderBadge provider={provider} />}
              {index > 0 && (
                <button type="button" className="logs-link" onClick={() => onPriority([id, ...status.priority.filter((p) => p !== id)])}>
                  {t("Put first")}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {coverage.length > 0 && (
        <div className="ui-table-wrap">
          <table className="ui-table logs-country-routing">
            <thead>
              <tr>
                <th>{t("Country")}</th>
                <th>{t("Served by")}</th>
                <th>{t("Choice")}</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((country) => (
                <tr key={country.code}>
                  <td>
                    <strong>{COUNTRY_NAMES[country.code] || country.name || country.code}</strong>
                    <span className="ui-media-meta">{country.currency}</span>
                  </td>
                  <td>
                    {country.effective ? (
                      <Badge color={PROVIDER_COLORS[country.effective] || "slate"}>{PROVIDER_LABELS[country.effective] || country.effective}</Badge>
                    ) : (
                      <Badge color="slate" dot>{t("Closed")}</Badge>
                    )}
                    <span className="ui-media-meta">
                      {t("Covered by")} {country.providers.map((p) => p.label).join(", ")}
                    </span>
                    {!["auto", "off"].includes(country.choice) && !country.providers.some((p) => p.id === country.choice) && (
                      <span className="ui-media-meta" data-error="">
                        {t("Waiting for {{name}} to open this country", { name: PROVIDER_LABELS[country.choice] || country.choice })}
                      </span>
                    )}
                  </td>
                  <td>
                    <select
                      className="ui-input logs-select"
                      aria-label={`${t("Provider for")} ${COUNTRY_NAMES[country.code] || country.name || country.code}`}
                      value={country.choice}
                      onChange={(e) => onCountry(country.code, e.target.value)}
                    >
                      <option value="auto">{t("Automatic (preference order)")}</option>
                      {configured.map((p) => {
                        const covers = country.providers.some((c) => c.id === p.id);
                        return (
                          <option key={p.id} value={p.id}>
                            {t("Only")} {p.label}
                            {!covers ? ` (${t("not open in this country yet")})` : !p.available ? ` (${t("suspended")})` : ""}
                          </option>
                        );
                      })}
                      <option value="off">{t("Closed (card only)")}</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!entries.length && (
        <p className="ui-hint" data-error="">{t("Mobile Money is closed everywhere: customers can only pay by card.")}</p>
      )}
    </section>
  );
};

/**
 * Taux de conversion des prix en dollars (formules, contenus exclusifs) vers les devises du Mobile
 * Money. Les devises des pays ouverts sont en tête ; un champ vide revient au taux par défaut.
 */
const Rates = ({ rates, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({});
  const [showAll, setShowAll] = useState(false);
  const sorted = [...rates].sort((a, b) => Number(b.used) - Number(a.used) || a.currency.localeCompare(b.currency));
  const visible = showAll ? sorted : sorted.filter((r) => r.used);
  const dirty = Object.keys(draft).length > 0;
  return (
    <section className="ui-card logs-panel">
      <header className="logs-panel-head">
        <h2>{t("Conversion rates")}</h2>
        {dirty && (
          <div className="logs-rates-actions">
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setDraft({})}>{t("Cancel")}</button>
            <button type="button" className="ui-btn" onClick={() => { onSave(draft); setDraft({}); }}>{t("Save")}</button>
          </div>
        )}
      </header>
      <p className="ui-hint">
        {t("Prices are set in dollars (Subscription plans). Mobile Money converts them at these rates, rounded up to a simple amount. Check the rates of the countries you open: the default values are indicative.")}
      </p>
      {!visible.length ? (
        <p className="ui-hint">{t("No Mobile Money country open.")}</p>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table logs-rates">
            <thead>
              <tr>
                <th>{t("Currency")}</th>
                <th>{t("1 $ =")}</th>
                <th>{t("Monthly")}</th>
                <th>{t("Annual")}</th>
                <th>{t("Source")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.currency}>
                  <td><strong>{r.currency}</strong></td>
                  <td>
                    <input
                      className="ui-input logs-rate-input"
                      type="number"
                      min="0"
                      step="any"
                      aria-label={`${t("Rate")} ${r.currency}`}
                      placeholder={String(r.defaultRate)}
                      value={draft[r.currency] !== undefined ? draft[r.currency] : r.source === "admin" ? r.rate : ""}
                      onChange={(e) => setDraft((d) => ({ ...d, [r.currency]: e.target.value === "" ? null : e.target.value }))}
                    />
                  </td>
                  <td>{formatAmount(r.monthly, r.currency)}</td>
                  <td>{formatAmount(r.annual, r.currency)}</td>
                  <td>
                    {r.source === "admin" ? <Badge color="blue">{t("Set by an admin")}</Badge> : <Badge color="slate">{t("Default")}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button type="button" className="logs-link" onClick={() => setShowAll((v) => !v)}>
        {showAll ? t("Only the currencies in use") : t("Show every currency")}
      </button>
    </section>
  );
};

/** Consultation en direct d'une transaction chez son prestataire (lecture seule). */
const Lookup = () => {
  const { t } = useTranslation();
  const [id, setId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async (e) => {
    e.preventDefault();
    if (!id.trim()) return;
    setLoading(true);
    try {
      setResult(await lookupMobile(id.trim()));
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
    setLoading(false);
  };
  const live = result?.live;
  return (
    <section className="ui-card logs-panel">
      <header className="logs-panel-head">
        <h2>{t("Look up a transaction")}</h2>
      </header>
      <form className="logs-lookup" onSubmit={search}>
        <input className="ui-input" value={id} placeholder={t("Transaction number (pawaPay identifier, CINETPAY_… or PAYSTACK_…)")} onChange={(e) => setId(e.target.value)} />
        <button type="submit" className="ui-btn" disabled={loading}>
          <i className="ri-search-eye-line" aria-hidden="true"></i>
          {loading ? t("Searching…") : t("Look up")}
        </button>
      </form>
      <p className="ui-hint">{t("Read only: nothing is changed. To apply the result, use “Recheck” in Failed payments.")}</p>
      {result && (
        <div className="logs-lookup-result">
          <div className="logs-compare">
            <div>
              <span className="ui-label">{t("At {{name}} (live)", { name: PROVIDER_LABELS[result.provider] || result.provider })}</span>
              {live?.error ? (
                <p className="ui-hint" data-error="">{live.error}</p>
              ) : (
                <>
                  <strong>{live?.status || live?.message || "—"}</strong>
                  <span className="ui-hint">
                    {formatAmount(live?.amount, live?.currency)} · {live?.operator || "—"}{live?.code ? ` · ${t("code")} ${live.code}` : ""}
                  </span>
                  {(live?.description || live?.message) && <span className="ui-hint">{live.description || live.message}</span>}
                </>
              )}
            </div>
            <div>
              <span className="ui-label">{t("At AFO MEDIA")}</span>
              {result.local ? (
                <>
                  <strong>{t(STATUS_BADGES[result.local.status]?.label || result.local.status)}</strong>
                  <span className="ui-hint">
                    {result.local.customer?.name} · {t(KIND_LABELS[result.local.kind] || result.local.kind)} · {formatAmount(result.local.amount, result.local.currency)}
                  </span>
                </>
              ) : (
                <span className="ui-hint">{t("Unknown in our database")}</span>
              )}
            </div>
          </div>
          {live?.state === "accepted" && result.local && result.local.status !== "SUCCESS" && (
            <p className="ui-hint" data-error="">{t("Paid at the provider but not on our side: use “Recheck” to deliver the service.")}</p>
          )}
          <h3 className="logs-subtitle">{t("History")}</h3>
          <Timeline events={result.events} />
        </div>
      )}
    </section>
  );
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "SUCCESS", label: "Succeeded" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const Transactions = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [provider, setProvider] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(null);
  const load = useCallback(() => getMobileTransactions({ status, provider, q: search, page }).then(setData), [status, provider, search, page]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);
  const items = data?.items || [];
  return (
    <section className="ui-card">
      <div className="ui-toolbar">
        <div className="ui-chips">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} type="button" className="ui-chip" aria-pressed={status === f.value} onClick={() => { setStatus(f.value); setPage(1); }}>
              {t(f.label)}
            </button>
          ))}
          <select className="ui-input logs-select" value={provider} aria-label={t("Provider")} onChange={(e) => { setProvider(e.target.value); setPage(1); }}>
            <option value="">{t("All providers")}</option>
            <option value="pawapay">pawaPay</option>
            <option value="cinetpay">CinetPay</option>
            <option value="paystack">Paystack</option>
          </select>
        </div>
        <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t("Transaction, name or email")} />
      </div>
      {!data ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState>{t("No Mobile Money transaction")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>{t("Transaction")}</th>
                <th>{t("Customer")}</th>
                <th>{t("Payment")}</th>
                <th>{t("Status")}</th>
                <th>{t("Date")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="ui-row-link" onClick={() => setDetails(p.id)}>
                  <td>
                    <code className="logs-code">{p.transactionId}</code>
                    <span className="ui-media-meta">{PROVIDER_LABELS[p.provider] || p.provider}</span>
                  </td>
                  <td>
                    <span className="ui-media-title">{p.customer?.name}</span>
                    <span className="ui-media-meta">{p.customer?.email || p.phone}</span>
                  </td>
                  <td>
                    <strong>{formatAmount(p.amount, p.currency)}</strong>
                    <span className="ui-media-meta">{t(KIND_LABELS[p.kind] || p.kind)}{p.operator ? ` · ${p.operator}` : ""}</span>
                  </td>
                  <td>
                    <Badge color={STATUS_BADGES[p.status]?.color || "slate"} dot>{t(STATUS_BADGES[p.status]?.label || p.status)}</Badge>
                    {p.status === "FAILED" && <span className="ui-media-meta logs-reason">{t(p.reason)}</span>}
                  </td>
                  <td className="ui-cell-muted">{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
      <PaymentDetails paymentId={details} onClose={() => setDetails(null)} />
    </section>
  );
};

/** Mobile Money : prestataires (pawaPay, CinetPay, Paystack), routage par pays, taux, consultation, transactions. */
const MobileMoney = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null); // { provider, suspend }

  const load = useCallback(() => getMobileStatus().then(setStatus), []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (changes, message) => {
    try {
      setStatus(await updateMobileSettings(changes));
      toast.success(message);
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    }
  };

  if (!status) return <TableSkeleton rows={3} />;
  return (
    <div className="logs-section">
      <Routing
        status={status}
        onPriority={(priority) => save({ priority }, t("Order updated"))}
        onCountry={(code, choice) => save({ countryRouting: { [code]: choice } }, t("Country routing updated"))}
      />
      <div className="logs-cinetpay-grid logs-providers">
        {status.providers.map((provider) => (
          <ProviderCard key={provider.id} provider={provider} onToggle={(p, suspend) => setPending({ provider: p, suspend })} />
        ))}
      </div>
      <Rates rates={status.rates || []} onSave={(rates) => save({ rates }, t("Conversion rates saved"))} />
      <Lookup />
      <Transactions />
      <ConfirmDialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        danger={pending?.suspend}
        onConfirm={() => save(
          { suspended: { [pending.provider.id]: pending.suspend } },
          pending.suspend ? t("{{name}} suspended", { name: pending.provider.label }) : t("{{name}} reactivated", { name: pending.provider.label })
        )}
        confirmLabel={pending?.suspend ? t("Suspend") : t("Reactivate")}
      >
        <strong>
          {pending?.suspend
            ? t("Suspend {{name}}?", { name: pending?.provider.label })
            : t("Reactivate {{name}}?", { name: pending?.provider.label })}
        </strong>
        <p>
          {pending?.suspend
            ? t("No new payment will open with it. Its countries switch to the other provider when it covers them, otherwise customers pay by card. Payments already open are still followed and delivered.")
            : t("It will receive new payments again, according to the routing order.")}
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default MobileMoney;
